import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { makeStyles } from '@material-ui/styles'
import { VmStorageOptions } from 'account/components/userManagement/tenants/VmStorageTypePicklist'
import { getDomainId } from 'api-client/helpers'
import { IPVersions } from 'app/constants'
import { blockStorageDoc, drrDoc, ephemeralSharedStorageDoc, serverGroupsDoc } from 'app/docLinks'
import useTenantSelector from 'app/plugins/account/components/userManagement/tenants/useTenantSelector'
import clsx from 'clsx'
import Alert from 'core/components/Alert'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import OptionsToggler from 'core/components/OptionsToggler'
import PaginatedSelectableCards from 'core/components/PaginatedSelectableCards'
import SelectableCard from 'core/components/SelectableCard'
import SimpleLink from 'core/components/SimpleLink'
import TitleWithInfoIcon from 'core/components/TitleWithInfoIcon'
import Progress from 'core/components/progress/Progress'
import CodeMirror from 'core/components/validatedForm/CodeMirrorField'
import { default as PicklistField } from 'core/components/validatedForm/DropdownField'
import Info from 'core/components/validatedForm/Info'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Slider from 'core/elements/Slider'
import Text from 'core/elements/Text'
import Dropdown from 'core/elements/dropdown'
import Radio from 'core/elements/input/Radio'
import ModalForm from 'core/elements/modal/ModalForm'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import Theme from 'core/themes/model'
import {
  customValidator,
  nonDecimalValueValidator,
  yamlValidator,
} from 'core/utils/fieldValidators'
import { isHotpluggableFlavor, isOpenCloud } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import * as IpAddress from 'ip-address'
import { isSelfServiceRole } from 'k8s/util/helpers'
import { listClusterHostGroups } from 'openstack/components/infrastructure/clusters/actions'
import { clusterHostGroupsSelector } from 'openstack/components/infrastructure/clusters/selectors'
import { humanReadableSize } from 'openstack/helpers'
import { ascend, prop, sortWith } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { durationBetweenDates, formatDate } from 'utils/misc'
import { trackEvent } from 'utils/tracking'
import ServiceUnhealthyInfo from '../common/ServiceUnhealthyInfo'
import { listFlavors } from '../flavors/actions'
import { flavorsSelector } from '../flavors/selectors'
import { imageColumns } from '../images/ImagesListPage'
import { listImages } from '../images/actions'
import { ImageDetailsPageTabs } from '../images/details/model'
import { imagesSelector } from '../images/selectors'
import { getBlueprints, listHostAggregates, listResmgrHosts } from '../infrastructure/actions'
import ClusterHostGroupsPicklist from '../infrastructure/clusters/ClusterHostGroupsPicklist'
import { imageLibrayHostSelector } from '../infrastructure/selectors'
import { listNetworks } from '../networks/actions'
import {
  MANAGED_NETWORK_INFO,
  NetworkMode,
  SIMPLE_NETWORK_TOOLTIP_INFO,
} from '../networks/constants'
import { listSecurityGroups } from '../networks/security-groups/actions'
import { securityGroupsSelector } from '../networks/security-groups/selectors'
import { networksSelector } from '../networks/selectors'
import { listVolumeSnapshots } from '../storage/snapshots/actions'
import {
  volumeSnapshotsByIdSelector,
  volumeSnapshotsSelector,
} from '../storage/snapshots/selectors'
import VolumeTypesPicklist from '../storage/volume-types/VolumeTypesPicklist'
import {
  listAvailableVolumes,
  listNonPaginatedVolumes,
  pollAvailableVolumes,
} from '../storage/volumes/actions'
import {
  availableVolumesSelector,
  nonPaginatedVolumesByIdSelector,
  nonPaginatedVolumesSelector,
} from '../storage/volumes/selectors'
import { IVolumeDetailsPageTabs } from '../storage/volumes/volume-details/model'
import MigrationPriorityPicklist from './MigrationPriorityPicklist'
import SourceTypesPicklist from './SourceTypesPicklist'
import SshKeyPicklist from './SshKeyPicklist'
import { IVirtualMachinesListPageTabs } from './VirtualMachinesListPage'
import VmCredentialsPanel from './VmCredentialsPanel'
import { createVirtualMachine, createVirtualMachines, listVirtualMachinesLite } from './actions'
import { getManagedNetworkColumns, getSimpleNetworkColumns } from './columns/networkColumns'
import {
  flavorColumns,
  flavorColumnsGpu,
  getVmSnapshotColumns,
  imageColumnsOrder,
  isFlavorCompatibleWithImageAndVolume,
  isImageCompatibleWithVolumeAndFlavor,
  securityGroupColumns,
  visibleFlavorColumns,
  visibleGpuFlavorColumns,
  visibleImageColumns,
  visibleVmSnapshotColumns,
  vmSnapshotColumnsOrder,
  volumeColumns,
} from './helpers'
import {
  ENABLE_SIMPLE_NETWORK_BATCH_CREATION,
  useSimpleNetworkPorts,
} from './hooks/useSimpleNetworkPorts'
import { virtualMachinesLiteByIdSelector } from './selectors'
import ServerGroupsPicklist from './server-groups/ServerGroupsPicklist'
import { VOLUME_BACKED_VM_WARNING_MSG } from './tooltipConstants'
import { buildVMRequestBody } from './utils/vmBodyBuilder'

const AVAILABLITY_ZONE_ERROR = 'The requested availability zone is not available'

const modalTitle = 'Deploy New VM'

const returnStepNumber = (source, step) =>
  !['image', 'snapshot'].includes(source) ? step : step - 1

export const placeholderCloudInitYaml = `#cloud-config
password: winterwonderland
chpasswd: { expire: False }
ssh_pwauth: True
manage_etc_hosts: true
runcmd:
- [ 'sh', '-c', 'echo "Hello World" > /tmp/helloworld.txt']
`

const allVmSourceTypeOptions = [
  { label: 'Image', value: 'image' },
  { label: 'New Volume', value: 'newVolume' },
  { label: 'Existing Volume', value: 'existingVolume' },
  { label: 'VM Snapshot', value: 'snapshot' },
  { label: 'Volume Snapshot', value: 'volumeSnapshot' },
]

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    display: 'grid',
    gap: 16,
    marginBottom: 32,
    height: 'fit-content',
  },
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
  info: {
    background: theme.palette.grey[50],
  },
  infoContent: {
    display: 'grid',
    gap: 8,
  },
  halfColumns: {
    display: 'grid',
    gap: 24,
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'baseline',
  },
  errorMessage: {
    display: 'grid',
    gridTemplateColumns: '26px auto',
    alignItems: 'center',
  },
  errorText: {
    color: theme.components.graph.error,
  },
  spaceBelow: {
    marginBottom: 16,
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: 16,
  },
  card: {
    height: 'calc(100% - 32px)',
    alignItems: 'center',
    padding: '16px 24px',
  },
  cardHeader: {
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    paddingBottom: 8,
    marginBottom: 8,
    display: 'grid',
    gridTemplateColumns: 'auto max-content',
    alignItems: 'center',
  },
  gpuTag: {
    background: theme.palette.blue[100],
    border: `1px solid ${theme.palette.blue[700]}`,
    borderRadius: 4,
    padding: '2px 8px',
    color: theme.palette.blue[700],
    fontWeight: 600,
  },
  cardText: {
    fontSize: 12,
  },
  pairs: {
    display: 'grid',
    gap: 4,
  },
  separator: {
    height: 1,
    width: '100%',
    background: theme.palette.grey[200],
    margin: '16px 0px',
  },
  networkModeCards: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  networkCardWrapper: {
    position: 'relative',
    width: '100%',
  },
  networkCard: {
    width: '100%',
    height: '100%',
  },
  networkCardBody: {
    display: 'grid',
    gridGap: theme.spacing(1),
    gridTemplateColumns: 'max-content 1fr max-content',
    padding: '16px',
    placeItems: 'center',
  },
  networkCardContent: {
    justifySelf: 'left',
  },
  networkInfoIcon: {
    color: theme.palette.grey[500],
    fontSize: '20px',
    cursor: 'help',
  },
  networkTooltipBody: {
    maxWidth: 250,
  },
  vmCount: {
    display: 'grid',
    gridTemplateColumns: 'auto 130px',
    gap: 16,
    alignItems: 'center',
  },
  vmCountDisabled: {
    cursor: 'not-allowed',
  },
  vmCountInput: {
    display: 'grid',
    gridTemplateColumns: 'auto max-content',
    alignItems: 'center',
    gap: 8,
  },
  vmCountText: {
    fontSize: 12,
  },
  tabbed: {
    marginLeft: 52,
  },
  passwordScreen: {
    display: 'grid',
    gap: 24,
    height: 'fit-content',
  },
  filters: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    gap: 16,
  },
  credentials: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: '1fr 6fr',
    alignItems: 'center',
  },
  warning: {
    background: theme.palette.yellow[100],
    display: 'grid',
    gap: theme.spacing(1),
  },
  switchTooltip: {
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
  tooltipContainer: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  tooltipLink: {
    color: theme.components.tooltip.text,
    textDecoration: 'underline',
    '& > span': {
      color: theme.components.tooltip.text,
    },
  },

  portToolTip: {
    height: ' 16px',
  },
}))

interface Props {
  addRoute: Route
  reloadVirtualMachines: () => void
}

export const getPrivateIpValidator = (network) => {
  return customValidator((value, formValues) => {
    if (!value) {
      return true
    }
    try {
      const subnets = network?.subnetDetails
      const pass = subnets?.some((subnet) => {
        const testCidr =
          subnet?.ip_version === IPVersions.IPv4
            ? new IpAddress.Address4(subnet?.cidr)
            : new IpAddress.Address6(subnet?.cidr)
        return subnet?.ip_version === IPVersions.IPv4
          ? new IpAddress.Address4(value).isInSubnet(testCidr)
          : new IpAddress.Address6(value).isInSubnet(testCidr)
      })
      return pass
    } catch (err) {
      return false
    }
  }, 'IP Address must be within one of the subnet CIDRs')
}

const countValidator = customValidator((value) => {
  return value >= 1 && value <= 20
}, 'Count must be between 1 and 20')

export const hotplugMemoryValidator = customValidator((value, formValues) => {
  return value > 0
}, 'Value must be greater than 0')

export const hotplugVcpuValidator = customValidator((value, formValues) => {
  return value > 0
}, 'Value must be greater than 0')

export const ErrorText = ({ children }) => {
  const classes = useStyles({})
  return (
    <div className={classes.errorMessage}>
      <FontAwesomeIcon>circle-exclamation</FontAwesomeIcon>
      <Text variant="body1" className={classes.errorText}>
        {children}
      </Text>
    </div>
  )
}

const SubnetsCellComponent = ({ value }) => {
  return (
    <>
      {value.map((subnet) => (
        <Text key={subnet.name} variant="body2">
          {subnet.name}: <b>{subnet.cidr}</b>
        </Text>
      ))}
    </>
  )
}

export default function CreateVirtualMachineModal({ addRoute, reloadVirtualMachines }: Props) {
  const { history, match } = useReactRouter()
  const classes = useStyles()

  // Simple Network port management hook
  const {
    preCreateSimpleNetworkPorts,
    cleanupPreCreatedPorts,
    hasSimpleNetworks,
    shouldEnableConfigDrive,
  } = useSimpleNetworkPorts()

  const [viewImageToggle, setViewImageToggle] = useState('list')
  const [viewFlavorToggle, setViewFlavorToggle] = useState('list')
  const [imageSort, setImageSort] = useState('')
  const [flavorSort, setFlavorSort] = useState('')
  const [showVmPassword, setShowVmPassword] = useState(false)
  const [vmPassword, setVmPassword] = useState('')
  const [imageType, setImageType] = useState('')
  const [blueprint, setBlueprint] = useState(null)
  const [minAvailableIpsNetwork, setMinAvailableIpsNetwork] = useState(null)
  const [vmNetworkMode, setVmNetworkMode] = useState<NetworkMode | null>(NetworkMode.MANAGED) // Default to Managed networks
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  // If boot_from_volume is false, disable the boot from volume option, undefined or any other value means it is enabled
  const disabledBootFromVolume = features?.experimental?.boot_from_volume === false

  const defaultParams = {
    name: '',
    sourceType: disabledBootFromVolume ? 'image' : 'newVolume',
    image: [],
    attachVolumes: [],
    volume: [],
    volumeSize: '20',
    volumeType: '',
    volumeSnapshot: [],
    flavor: [],
    deleteOnTermination: false,
    availabilityZone: null,
    enableHotplugging: false,
    gpuCompatible: false,
    networks: [],
    selectedPorts: {},
    fixedIPs: {},
    count: 1,
    sshKey: null,
    serverGroup: null,
    useCloudInit: false,
    useSecurityGroups: false,
    cloudInitYaml: placeholderCloudInitYaml,
    securityGroups: [],
    metadata: [],
    hotplugMemory: null,
    hotplugVcpu: null,
    priority: '',
  }
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs
  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const isSelfServiceUser = isSelfServiceRole(session)

  const { loading: loadingImages } = useListAction(listImages, {
    params: {},
  })
  const images = useSelector(imagesSelector)

  const { loading: loadingVolumes, reload: reloadVolumes } = useListAction(
    listNonPaginatedVolumes,
    {
      params: {},
    },
  )
  const volumes = useSelector(nonPaginatedVolumesSelector)

  // Available volumes list to show for Existing Volume source type and Attach Volumes list
  const { loading: loadingAvailableVolumes, reload: reloadAvailableVolumes } = useListAction(
    listAvailableVolumes,
    {
      loadOnDemand: true,
    },
  )
  const { updating: updatingAvailableVolumes, update: updateAvailableVolumes } = useUpdateAction(
    pollAvailableVolumes,
  )
  const availableVolumesRaw = useSelector(availableVolumesSelector)

  // Volume list to show for Existing Volume source type
  const bootableVolumesList = availableVolumesRaw.filter((volume) => volume.bootable !== 'false')

  // List of available volumes to show for Attach Volumes list
  // And should not contain volumes selected as source type
  const availableVolumes = useMemo(
    () =>
      availableVolumesRaw?.filter((volume) => {
        if (volume.status !== 'available') return false
        if (params.sourceType !== 'existingVolume') return true
        return !params.volume.includes(volume)
      }),
    [availableVolumesRaw, params.sourceType, params.volume],
  )

  const volumesById = useSelector(nonPaginatedVolumesByIdSelector)

  const { loading: loadingFlavors } = useListAction(listFlavors, {
    params: {},
  })
  const flavors = useSelector(flavorsSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots, {
    params: {},
  })
  const volumeSnapshots = useSelector(volumeSnapshotsSelector)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)

  const { loading: loadingNetworks } = useListAction(listNetworks, {
    params: {},
    initialLoadingState: true, // for the useEffect that is tracking loading
  })
  const _networksList = useSelector(networksSelector)
  const networksList = _networksList.filter((network) => {
    // Include Simple Networks (L2-only, no subnets) AND Managed Networks (with subnets)
    const isManagedNetwork = network.subnets?.length > 0
    const isSimpleNet = network.isSimpleNetwork

    // Must be either Managed or Simple network
    if (!isManagedNetwork && !isSimpleNet) return false

    // If network is shared, show it
    if (network.shared) return true

    // For networks in current tenant
    if (network.tenant_id === currentTenant) {
      // For self service users, hide external networks
      if (isSelfServiceUser) return !network?.['router:external']
      // For non-self service users, show all networks
      return true
    }

    // Hide networks not in current tenant
    return false
  })

  // Calculate network counts for toggle cards
  const managedNetworksCount = useMemo(() => {
    return networksList.filter((network) => !network.isSimpleNetwork && network.subnets?.length > 0)
      .length
  }, [networksList])

  const simpleNetworksCount = useMemo(() => {
    return networksList.filter((network) => network.isSimpleNetwork).length
  }, [networksList])

  const { loading: loadingSecurityGroups } = useListAction(listSecurityGroups, {
    params: {},
  })
  const securityGroupsList = useSelector(securityGroupsSelector)

  // Check if image library host is available in the host list
  const { loading: loadingResmgrHosts } = useListAction(listResmgrHosts, {
    params,
  })
  const imageLibraryHost = useSelector(imageLibrayHostSelector)
  const isImageLibraryHostAvailable = !!imageLibraryHost

  const { loading: loadingVms } = useListAction(listVirtualMachinesLite)
  const vmsById = useSelector(virtualMachinesLiteByIdSelector)

  const { loading: loadingHostAggregates } = useListAction(listHostAggregates)

  const { update, updating, error, reset } = useUpdateAction(createVirtualMachine)
  const {
    update: createMultiple,
    updating: creatingMultiple,
    error: errorMultiple,
    reset: resetMultiple,
  } = useUpdateAction(createVirtualMachines)
  const responseError = error || errorMultiple

  // Check if modal is open
  const isOpen = addRoute.pattern.match(match.url)

  const { loading: loadingClusterHostGroups } = useListAction(listClusterHostGroups)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)

  const { data: tenants, reload: reloadTenants } = useTenantSelector(getDomainId())

  useEffect(() => {
    const queryBlueprint = async () => {
      try {
        const blueprints = await getBlueprints()
        const blueprint = blueprints?.[0]
        setBlueprint(blueprint)
      } catch {
        setBlueprint(null)
      }
    }

    queryBlueprint()
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // Reload available volumes to ensure we have the latest volumes
    updateAvailableVolumes()

    // Reload tenents to ensure we have the latest vm_storage_type configuration
    reloadTenants(true)

    // After everything is loaded, track object numbers
    if (
      !loadingImages &&
      !loadingVolumes &&
      !loadingFlavors &&
      !loadingNetworks &&
      !loadingClusterHostGroups
    ) {
      const body = {
        images: images?.length,
        volumes: volumes?.length,
        flavors: flavors?.length,
        networks: networksList?.length,
        networksWithFreeIps: networksList?.filter((network) => network?.availableIps > 0)?.length,
        clusters: clusterHostGroups?.length,
      }
      trackEvent(`${modalTitle} - Resources Present`, body)
    }
  }, [
    loadingImages,
    loadingVolumes,
    loadingFlavors,
    loadingNetworks,
    loadingClusterHostGroups,
    match.url,
  ])

  // Get current tenant details to check vm_storage_type configuration
  const tenant = tenants.find((tenant) => tenant.id === currentTenant)
  const tenantVmStorageType = tenant?.tags
    ?.find((tag) => tag.startsWith('vm_storage_type='))
    ?.split('=')[1]

  // Filter source type options based on tenant's vm_storage_type configuration
  const sourceTypeOptions = useMemo(() => {
    // Ephemeral source types: image, snapshot
    const ephemeralOptions = allVmSourceTypeOptions.filter((opt) =>
      ['image', 'snapshot'].includes(opt.value),
    )

    // Volume source types: newVolume, existingVolume, volumeSnapshot
    const volumeOptions = allVmSourceTypeOptions.filter((opt) =>
      ['newVolume', 'existingVolume', 'snapshot', 'volumeSnapshot'].includes(opt.value),
    )

    switch (tenantVmStorageType) {
      case VmStorageOptions.ephemeral:
        return ephemeralOptions
      case VmStorageOptions.volume:
        return volumeOptions
      case VmStorageOptions.ephemeral_and_volume:
      default:
        return allVmSourceTypeOptions
    }
  }, [tenantVmStorageType])

  useEffect(() => {
    if (params?.sourceType === 'existingVolume' || params.attachVolumes.length > 0) {
      updateParams({
        count: 1,
      })
    }
  }, [params.sourceType, params.attachVolumes])

  const allowedVmSnapshotTypes = useMemo(() => {
    switch (tenantVmStorageType) {
      case VmStorageOptions.ephemeral:
        return ['VM Ephemeral Disk Snapshot']
      case VmStorageOptions.volume:
        return ['VM Volume Snapshot']
      case VmStorageOptions.ephemeral_and_volume:
      default:
        return ['VM Ephemeral Disk Snapshot', 'VM Volume Snapshot']
    }
  }, [tenantVmStorageType])

  const filteredImages = useMemo(() => {
    const filtered = images.filter((image) => {
      if (image.status !== 'active') {
        return false
      }
      const isEphemeralSnapshot = image.imageType === 'ephemeralDiskSnapshot'
      const isVolumeSnapshot = image.imageType === 'vmVolumeSnapshot'
      if (params.sourceType === 'snapshot') {
        // If no filter is selected, filter based on tenant's allowed storage types
        if (
          imageType === 'VM Ephemeral Disk Snapshot' ||
          tenantVmStorageType === VmStorageOptions.ephemeral
        ) {
          return isEphemeralSnapshot
        } else if (
          imageType === 'VM Volume Snapshot' ||
          tenantVmStorageType === VmStorageOptions.volume
        ) {
          return isVolumeSnapshot
        } else {
          return isEphemeralSnapshot || isVolumeSnapshot
        }
      }
      return !(isEphemeralSnapshot || isVolumeSnapshot)
    })
    const sorter = {
      name: sortWith([ascend(prop('name'))]),
      virtualSize: sortWith([ascend(prop('virtual_size'))]),
    }[imageSort]
    return imageSort ? sorter(filtered) : filtered
  }, [images, imageSort, params.sourceType, imageType, allowedVmSnapshotTypes, tenantVmStorageType])

  const getVolumeSnapshotSourceVolume = useCallback(
    (volumeSnapshot) => {
      return volumesById[volumeSnapshot?.volume_id]
    },
    [volumesById],
  )

  /**
   * Create batch VMs with Simple Networks sequentially
   * Each VM creation handles its own port cleanup on failure via closure
   */
  const createBatchVMsWithSimpleNetworks = async (
    portsByVm: Array<{ [networkId: string]: string }>,
  ): Promise<void> => {
    const createdVmIds: string[] = []

    for (let i = 0; i < params.count; i++) {
      const vmPorts = portsByVm[i] || {}
      const vmName = params.count > 1 ? `${params.name}-${i + 1}` : params.name

      try {
        const body = buildVMRequestBody(params as any, { preCreatedPorts: vmPorts, vmName })
        const { success, response } = await update({ body })

        if (success) {
          createdVmIds.push(response?.id)
          console.log(`[Batch] VM ${i + 1}/${params.count} created: ${response?.id}`)
        } else {
          console.error(`[Batch] VM ${i + 1}/${params.count} failed`)
          await cleanupPreCreatedPorts(vmPorts)
        }
      } catch (error) {
        console.error(`[Batch] VM ${i + 1}/${params.count} error:`, error)
        await cleanupPreCreatedPorts(vmPorts)
      }
    }

    console.log(`[Batch] Complete: ${createdVmIds.length}/${params.count} succeeded`)

    if (createdVmIds.length > 0) {
      trackEvent(`${modalTitle} - Finished`, { vmIds: createdVmIds, batchSize: params.count })
      reloadVirtualMachines()
      handleClose()
    }
  }

  const submitForm = useCallback(async () => {
    // We should consider the smallest number of available IPs
    // from all the selected MANAGED networks. For example, if 5 networks are selected with 1 available IP each,
    // the limiting factor is the smallest available IP (1), so we can only create 1 VM in this case.
    // NOTE: Simple Networks are excluded from this check as they pre-create ports
    const managedNetworksOnly = params?.networks?.filter((network) => !network.isSimpleNetwork)

    if (managedNetworksOnly.length > 0) {
      const networkWithMinIps = managedNetworksOnly.reduce((minNet, currentNet) => {
        return currentNet?.availablePortAndIPs < minNet?.availablePortAndIPs ? currentNet : minNet
      })
      setMinAvailableIpsNetwork(networkWithMinIps)
      // If the smallest available IPs across MANAGED networks is less than the VM count, return early
      if (networkWithMinIps?.availablePortAndIPs < params.count) {
        return
      }
    } else {
      setMinAvailableIpsNetwork(null)
    }

    // Pre-create ports for Simple Networks (L2-only networks without IP pools)
    let preCreatedPorts: any = {} // Can be single map or array of maps
    const hasSimpleNetworks = params.networks.some((network) => network.isSimpleNetwork)

    if (hasSimpleNetworks) {
      try {
        preCreatedPorts = await preCreateSimpleNetworkPorts(
          params.networks,
          params.count,
          currentTenant,
          params.name,
        )
      } catch (error) {
        console.error('Layer 2 Network port pre-creation failed:', error)
        return // Exit early if port creation fails
      }
    }

    // Check if we need to create VMs sequentially (batch Simple Networks)
    const needsSequentialCreation =
      ENABLE_SIMPLE_NETWORK_BATCH_CREATION && hasSimpleNetworks && params.count > 1

    if (needsSequentialCreation) {
      // Batch VMs with Simple Networks: Create VMs one by one in a loop
      return await createBatchVMsWithSimpleNetworks(
        preCreatedPorts as Array<{ [networkId: string]: string }>,
      )
    }

    try {
      // Build VM request body using shared utility
      const body = buildVMRequestBody(params as any, {
        preCreatedPorts,
      })
      const updateFn = params.count === 1 ? update : createMultiple
      const { success, response } = await updateFn({ body })
      if (success) {
        const eventBody =
          params.count > 1
            ? {
                vmIds: response?.map((res) => res?.id || 'N/A'),
              }
            : {
                vmId: response?.id,
              }
        trackEvent(`${modalTitle} - Finished`, eventBody)
        reloadVirtualMachines()
        if (params.count > 1) {
          handleClose()
          return
        }
        // If not a windows VM, also close the wizard
        let isWindows = false
        if (['image', 'newVolume', 'snapshot'].includes(params.sourceType)) {
          isWindows = params?.image?.[0]?.os_type?.toLowerCase() === 'windows'
        } else if (params.sourceType === 'existingVolume') {
          isWindows =
            params?.volume?.[0]?.volume_image_metadata?.os_type?.toLowerCase() === 'windows'
        } else if (params.sourceType === 'volumeSnapshot') {
          const volSnapshot = params?.volumeSnapshot?.[0]
          const sourceVol = getVolumeSnapshotSourceVolume(volSnapshot)
          isWindows = sourceVol?.volume_image_metadata?.os_type?.toLowerCase() === 'windows'
        }
        if (!isWindows) {
          handleClose()
          return
        }
        setVmPassword(response?.adminPass)
        setShowVmPassword(true)
      } else {
        trackEvent(`${modalTitle} - Create API Failure`)
        // Cleanup pre-created ports if VM creation failed
        await cleanupPreCreatedPorts(preCreatedPorts)
      }
    } catch (vmCreationError) {
      console.error('VM creation failed:', vmCreationError)
      // Cleanup pre-created ports if VM creation failed
      await cleanupPreCreatedPorts(preCreatedPorts)
      // Re-throw to show error to user
      throw vmCreationError
    }
  }, [params, getVolumeSnapshotSourceVolume, currentTenant])

  const handleClose = () => {
    history.push(
      routeManager.routes.openstack.vms.path({
        vmstab: IVirtualMachinesListPageTabs.VirtualMachinesList,
      }),
    )
    setShowVmPassword(false)
    setVmPassword('')
    reset()
    resetMultiple()
    setImageSort('')
    setFlavorSort('')
    setParams(defaultParams)
  }

  // If sourceType is newVolume, existingVolume, or volumeSnapshot, only show flavors with 0 disk
  // If sourceType is snapshot and imageType is vmVolumeSnapshot, only show flavors with 0 disk
  const isVolumeSourceType = useMemo(
    () =>
      ['newVolume', 'existingVolume', 'volumeSnapshot'].includes(params.sourceType) ||
      (['snapshot'].includes(params.sourceType) &&
        params.image?.[0]?.imageType === 'vmVolumeSnapshot'),
    [params.sourceType, params.image],
  )

  const filteredFlavors = useMemo(() => {
    // Filter flavors based on hotplugging and gpu compatibility
    // If sourceType is newVolume, existingVolume, or volumeSnapshot, only show flavors with 0 disk
    // If sourceType is image or an ephemeralDiskSnapshot/image vm snapshot do not show flavors with 0 disk
    const filtered = flavors.filter((flavor) => {
      const isHotpluggable = isHotpluggableFlavor(flavor)

      if (params.enableHotplugging !== isHotpluggable) {
        return false
      }
      if (flavor?.gpuFlavor !== params.gpuCompatible) {
        return false
      }

      return isVolumeSourceType ? flavor?.disk === 0 : flavor?.disk !== 0
    })

    const sorter = {
      name: sortWith([ascend(prop('name'))]),
      vcpus: sortWith([ascend(prop('vcpus'))]),
      ram: sortWith([ascend(prop('ram'))]),
      disk: sortWith([ascend(prop('disk'))]),
    }[flavorSort]
    return flavorSort ? sorter(filtered) : filtered
  }, [flavors, flavorSort, params.enableHotplugging, params.gpuCompatible, isVolumeSourceType])

  const renderFlavorCard = useMemo(
    () => (item) => {
      // Flavor must be larger than the image size
      const disabled = isFlavorCompatibleWithImageAndVolume(item, params)
      return (
        <SelectableCard
          id={item}
          onClick={(item) =>
            updateParams({
              flavor: [item],
            })
          }
          tooltip={disabled ? 'Flavor disk size is smaller than the selected image' : undefined}
          active={params.flavor?.[0]?.id === item.id}
          className={classes.card}
          disabled={disabled}
          withCustomBody
          showRadioButton
        >
          <Text variant="body2" className={classes.cardHeader}>
            <b>{item.name}</b>
            {item?.gpuFlavor && <div className={classes.gpuTag}>GPU</div>}
          </Text>
          <div className={classes.pairs}>
            <Text variant="body2" className={classes.cardText}>
              vCPUs: <b>{item?.vcpus}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              RAM: <b>{item?.ram && humanReadableSize(item?.ram * 1024 * 1024)}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Disk: <b>{item?.disk && humanReadableSize(item?.disk * 1024 * 1024 * 1024)}</b>
              {disabled && ' (Too Small)'}
            </Text>
          </div>
        </SelectableCard>
      )
    },
    [params.image, params.flavor, params.sourceType],
  )

  const renderImageCard = useMemo(
    () => (item) => {
      const disabled = isImageCompatibleWithVolumeAndFlavor(item, params)
      return (
        <SelectableCard
          id={item}
          onClick={(item) =>
            updateParams({
              image: [item],
            })
          }
          tooltip={
            disabled
              ? `Image is not compatible with the selected flavor and volume size.`
              : undefined
          }
          active={params.image?.[0]?.id === item.id}
          className={classes.card}
          disabled={disabled}
          withCustomBody
          showRadioButton
        >
          <Text variant="body2" className={classes.cardHeader}>
            <b>{item.name}</b>
          </Text>
          <div className={classes.pairs}>
            <Text variant="body2" className={classes.cardText}>
              Format: <b>{item?.disk_format}</b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Virtual Size:{' '}
              <b>
                {item?.virtual_size ? humanReadableSize(item?.virtual_size) : 'N/A'}
                {disabled && ' (Too Large)'}
              </b>
            </Text>
            <Text variant="body2" className={classes.cardText}>
              Age:{' '}
              <b>
                {item?.created_at &&
                  durationBetweenDates({
                    labels: ['d'],
                  })(item?.created_at)}
              </b>
            </Text>
          </div>
        </SelectableCard>
      )
    },
    [params.image, params.volumeSize, params.sourceType, params.flavor],
  )

  // Network columns - use extracted column definitions
  const simpleNetworkColumns = useMemo(() => getSimpleNetworkColumns(), [])

  const managedNetworkColumns = useMemo(
    () => getManagedNetworkColumns({ params, getParamsUpdater }),
    [params.selectedPorts, params.fixedIPs],
  )

  // Select columns based on network mode
  const networkColumns = useMemo(() => {
    return vmNetworkMode === NetworkMode.SIMPLE ? simpleNetworkColumns : managedNetworkColumns
  }, [vmNetworkMode, simpleNetworkColumns, managedNetworkColumns])

  // Filter network list based on selected mode
  const filteredNetworksList = useMemo(() => {
    if (vmNetworkMode === NetworkMode.SIMPLE) {
      return networksList.filter((network) => network.isSimpleNetwork)
    } else if (vmNetworkMode === NetworkMode.MANAGED) {
      return networksList.filter(
        (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
      )
    }
    return networksList
  }, [vmNetworkMode, networksList])

  const vmSnapshotColumns = useMemo(() => {
    return getVmSnapshotColumns({
      vmsById,
      volumeSnapshotsById,
    })
  }, [vmsById, volumeSnapshotsById])

  const desiredLocalImageColumns = ['name', 'disk_format', 'virtual_size', 'created_at']

  const imageColumnsLocal = useMemo(() => {
    return imageColumns(false)
      .map((col) => ({
        ...col,
        id: col.key,
      }))
      .filter((col) => desiredLocalImageColumns.includes(col.key))
  }, [imageColumns])

  const volumeSnapshotColumns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
      },
      {
        id: 'id',
        label: 'UUID',
        display: false,
      },
      {
        id: 'description',
        label: 'Description',
      },
      {
        id: 'status',
        label: 'Status',
      },
      {
        id: 'size',
        label: 'Size',
        render: (size) => humanReadableSize(size * 1024 * 1024 * 1024),
      },
      {
        id: 'volume_id',
        label: 'Source Volume',
        render: (volumeId) => (
          <SimpleLink
            src={routeManager.routes.openstack.volumeDetails.path({
              id: volumeId,
              tab: IVolumeDetailsPageTabs.Overview,
            })}
          >
            {volumesById?.[volumeId]?.name || volumeId}
          </SimpleLink>
        ),
        display: false,
      },
      {
        id: 'attachedVmSnapshot.name',
        label: 'VM Snapshot',
        render: (name, item) => {
          if (!item?.attachedVmSnapshot) {
            return null
          }

          return (
            <SimpleLink
              src={routeManager.routes.openstack.imageDetails.path({
                id: item?.attachedVmSnapshot?.id,
                tab: ImageDetailsPageTabs.Overview,
              })}
            >
              {name}
            </SimpleLink>
          )
        },
        display: false,
      },
      {
        id: 'volumeType',
        label: (
          <TitleWithInfoIcon
            titleText="Volume Type"
            tooltipMsg={<div>This information is inherited from the source volume</div>}
          />
        ),
        render: (_, item) => {
          const volume = volumesById?.[item?.volume_id]
          return volume?.volume_type
        },
      },
      {
        id: 'bootable',
        label: (
          <TitleWithInfoIcon
            titleText="Bootable"
            tooltipMsg={<div>This information is inherited from the source volume</div>}
          />
        ),
        render: (_, item) => {
          const volume = volumesById?.[item?.volume_id]
          return volume?.bootable
        },
      },
      {
        id: 'created_at',
        label: 'Created',
        render: (value) => formatDate(value),
      },
    ],
    [volumesById],
  )

  const disableMultipleVMs = useMemo(() => {
    if (params.sourceType === 'existingVolume')
      return {
        disabled: true,
        message: 'Bulk VM Creation is not possible if VM is created from existing volume  ',
      }

    if (params.attachVolumes.length > 0)
      return {
        disabled: true,
        message: 'Bulk VM Creation is not possible if additional volumes are attached to the VM',
      }

    if (params?.networks?.some((network) => params?.selectedPorts[network.id]))
      return {
        disabled: true,
        message: 'You can create only 1 VM when a specific port is selected from a network.',
      }

    // Disable batch VM creation with Simple Networks (if feature flag is off)
    if (
      !ENABLE_SIMPLE_NETWORK_BATCH_CREATION &&
      params?.networks?.some((network) => network.isSimpleNetwork)
    )
      return {
        disabled: true,
        message:
          'Batch VM creation with Layer 2 Networks is not supported. Please create VMs one at a time.',
      }

    return {
      disabled: false,
      message: '',
    }
  }, [params?.networks, params?.selectedPorts, params?.attachVolumes, params?.sourceType])

  useEffect(() => {
    if (showVmPassword) {
      const modalBody = document.querySelector('.modal-body')
      modalBody.scrollTo(0, 0)
    }
  }, [showVmPassword])

  const isVMUsingEphemeralRootDisk = useMemo(
    () =>
      params?.sourceType === 'image' ||
      (params?.sourceType === 'snapshot' &&
        params?.image?.[0] &&
        params?.image[0]?.imageType === 'ephemeralDiskSnapshot'),
    [params?.sourceType, params.image],
  )

  return (
    <ModalForm
      route={addRoute}
      title={modalTitle}
      onSubmit={showVmPassword ? handleClose : submitForm}
      onClose={handleClose}
      submitting={updating || creatingMultiple}
      steps={returnStepNumber(params.sourceType, 5)}
      error={
        responseError?.message === AVAILABLITY_ZONE_ERROR
          ? {
              message: `The selected cluster '${params.availabilityZone}' does not have any hosts assigned. Please assign a host to it or select a different cluster`,
            }
          : responseError
      }
      submitTitle={showVmPassword ? `Finish` : `Deploy Virtual Machine`}
      hideBack={showVmPassword}
      maxWidth={900}
      showConfirmationOnCancel
    >
      {({ step }) => (
        <>
          {!showVmPassword ? (
            <>
              {step === 1 && (
                <div className={classes.fields}>
                  <ServiceUnhealthyInfo action="VM Creation" hypervisor imageLibrary blockStorage />
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Basic VM Info</Text>
                      <Text variant="body2">
                        Create a new virtual machine by selecting the desired VM source and target
                        cluster.
                      </Text>
                    </div>
                  </Info>
                  <div className={classes.halfColumns}>
                    <TextField
                      id="name"
                      label="Name"
                      onChange={getParamsUpdater('name')}
                      value={params.name}
                      info="Choose a name for your VM"
                      required
                    />
                    <PicklistField
                      id="availabilityZone"
                      label="Cluster"
                      info="Choose the virtualized cluster that this VM should be placed on"
                      onChange={getParamsUpdater('availabilityZone')}
                      value={params.availabilityZone}
                      disableClusterWithoutHosts
                      fullWidth
                      required={isVmware && isOpenCloud() ? false : true}
                      DropdownComponent={ClusterHostGroupsPicklist}
                    />
                  </div>
                  <div className={classes.halfColumns}>
                    <PicklistField
                      DropdownComponent={SourceTypesPicklist}
                      id="sourceType"
                      label="Boot VM From"
                      onChange={(value) =>
                        updateParams({
                          sourceType: value,
                          image: [],
                          attachVolumes: [],
                          volume: [],
                        })
                      }
                      value={params.sourceType}
                      options={sourceTypeOptions}
                      required
                    />
                  </div>
                  {params.sourceType === 'volumeSnapshot' && (
                    <ToggleSwitchField
                      id="deleteOnTermination"
                      value={params.deleteOnTermination}
                      onChange={getParamsUpdater('deleteOnTermination')}
                      label="Delete Volume on VM deletion"
                      customTooltipInfoBody={
                        <Text variant="body2" className={classes.switchTooltip}>
                          If enabled, the VM boot volume will be deleted when the VM is deleted. If
                          disabled, the volume will remain after the VM is deleted.
                        </Text>
                      }
                    />
                  )}
                  {/* If the source type is image or ephemeral disk snapshot show this warning */}
                  {isVMUsingEphemeralRootDisk ? (
                    <>
                      {!blueprint?.instanceSharedStorage && (
                        <Alert variant="warning" className={classes.warning}>
                          <Text variant="caption1">High Availability Warning</Text>
                          <Text variant="body2">
                            This VM configuration uses ephemeral storage for the root disk. If a
                            host fails, the High Availability service cannot recover the data on
                            this VM. For data recovery protection, use{' '}
                            <ExternalLink url={blockStorageDoc}>Block Storage Volumes</ExternalLink>{' '}
                            or{' '}
                            <ExternalLink url={ephemeralSharedStorageDoc}>
                              Ephemeral Shared Storage
                            </ExternalLink>{' '}
                            for the root disk.
                          </Text>
                        </Alert>
                      )}
                      <Alert variant="warning" className={classes.warning}>
                        <Text variant="body2">{VOLUME_BACKED_VM_WARNING_MSG}</Text>
                      </Alert>
                    </>
                  ) : null}
                  {['image', 'snapshot', 'newVolume'].includes(params.sourceType) && (
                    <>
                      {params.sourceType === 'newVolume' && (
                        <>
                          <div className={classes.halfColumns}>
                            <TextField
                              id="volumeSize"
                              label="Volume Size (GiB)"
                              onChange={(value) => {
                                getParamsUpdater('volumeSize')(value)
                                if (
                                  !isImageCompatibleWithVolumeAndFlavor(params.image, {
                                    ...params,
                                    volumeSize: value,
                                  })
                                ) {
                                  updateParams({
                                    image: [],
                                  })
                                }
                              }}
                              value={params.volumeSize}
                              info="Size of the primary VM volume"
                              type="number"
                              min={0}
                              integer
                              required
                            />
                            <PicklistField
                              id="volumeType"
                              DropdownComponent={VolumeTypesPicklist}
                              value={params.volumeType}
                              onChange={getParamsUpdater('volumeType')}
                              required
                            />
                          </div>
                          <ToggleSwitchField
                            id="deleteOnTermination"
                            value={params.deleteOnTermination}
                            onChange={getParamsUpdater('deleteOnTermination')}
                            label="Delete Volume on VM deletion"
                            customTooltipInfoBody={
                              <Text variant="body2" className={classes.switchTooltip}>
                                If enabled, the VM boot volume will be deleted when the VM is
                                deleted. If disabled, the volume will remain after the VM is
                                deleted.
                              </Text>
                            }
                          />
                        </>
                      )}
                      <div className={classes.separator} />
                      <Text variant="subtitle2">Image</Text>
                      {isImageLibraryHostAvailable ? (
                        <>
                          <Text variant="body2">
                            Only images that are fully available and ready to use will be shown
                            below.
                          </Text>
                          <Progress overlay loading={loadingResmgrHosts} message="Loading...">
                            {viewImageToggle === 'grid' && (
                              <PaginatedSelectableCards
                                renderSelectableCardComponent={renderImageCard}
                                data={filteredImages}
                                searchProps={['name', 'disk_format']}
                                pluralLabel="Images"
                                extraHeaderContent={
                                  <OptionsToggler
                                    options={[
                                      {
                                        icon: 'list',
                                        value: 'list',
                                      },
                                      {
                                        icon: 'grid',
                                        value: 'grid',
                                      },
                                    ]}
                                    value={viewImageToggle}
                                    onChange={setViewImageToggle}
                                  />
                                }
                                sortDropdown={
                                  <Dropdown
                                    name="sort"
                                    value={imageSort}
                                    items={[
                                      {
                                        label: 'Select...',
                                        value: '',
                                      },
                                      {
                                        label: 'Name',
                                        value: 'name',
                                      },
                                      {
                                        label: 'Virtual Size',
                                        value: 'virtualSize',
                                      },
                                    ]}
                                    onChange={(value) => setImageSort(value)}
                                  ></Dropdown>
                                }
                              />
                            )}
                            {viewImageToggle === 'list' && (
                              <>
                                <div className={classes.spaceBelow}>
                                  <OptionsToggler
                                    options={[
                                      {
                                        icon: 'list',
                                        value: 'list',
                                      },
                                      {
                                        icon: 'grid',
                                        value: 'grid',
                                      },
                                    ]}
                                    value={viewImageToggle}
                                    onChange={setViewImageToggle}
                                  />
                                  {params.sourceType === 'snapshot' &&
                                    allowedVmSnapshotTypes.length > 1 && (
                                      <Dropdown
                                        label="Filter By Type:"
                                        compact
                                        showAll
                                        allKey=""
                                        items={allowedVmSnapshotTypes.map((type) => ({
                                          value: type,
                                        }))}
                                        value={imageType}
                                        onChange={(val) => setImageType(val)}
                                      />
                                    )}
                                </div>
                                <ListTableField
                                  id="image"
                                  data={filteredImages}
                                  loading={loadingImages}
                                  columns={
                                    params.sourceType === 'snapshot'
                                      ? vmSnapshotColumns
                                      : imageColumnsLocal
                                  }
                                  visibleColumns={
                                    params.sourceType === 'snapshot'
                                      ? visibleVmSnapshotColumns
                                      : visibleImageColumns
                                  }
                                  columnsOrder={
                                    params.sourceType === 'snapshot'
                                      ? vmSnapshotColumnsOrder
                                      : imageColumnsOrder
                                  }
                                  onChange={getParamsUpdater('image')}
                                  checkboxCond={(image) =>
                                    !isImageCompatibleWithVolumeAndFlavor(image, params)
                                  }
                                  checkboxCondMessage="Image size is larger than the volume size specified"
                                  value={params.image}
                                  uniqueIdentifier="id"
                                  searchTargets={['name', 'disk_format', 'imageTypeDisplay']}
                                  // noScroll
                                  required
                                />
                              </>
                            )}
                          </Progress>
                        </>
                      ) : (
                        <ErrorText>No image library found. Choose a different source</ErrorText>
                      )}
                    </>
                  )}
                  {params.sourceType === 'existingVolume' && (
                    <>
                      <div className={classes.separator} />
                      <Text variant="subtitle2">Volume</Text>
                      <Text variant="body2">
                        Only <b>bootable</b> volumes with <b>available</b> status may be selected
                        below.
                      </Text>
                      <ListTableField
                        id="volume"
                        data={bootableVolumesList}
                        loading={loadingAvailableVolumes || updatingAvailableVolumes}
                        onReload={reloadAvailableVolumes}
                        columns={volumeColumns}
                        onChange={getParamsUpdater('volume')}
                        value={params.volume}
                        uniqueIdentifier="id"
                        searchTargets={['name', 'status', 'volume_type', 'description']}
                        checkboxCond={(volume) =>
                          volume.status === 'available' && volume.bootable === 'true'
                        }
                        required
                      />
                    </>
                  )}
                  {params.sourceType === 'volumeSnapshot' && (
                    <>
                      <div className={classes.separator} />
                      <Text variant="subtitle2">Volume Snapshot</Text>
                      <Text variant="body2">
                        Only <b>bootable</b> volumes snapshots with <b>available</b> status may be
                        selected below.
                      </Text>
                      <ListTableField
                        id="volumeSnapshot"
                        data={volumeSnapshots}
                        loading={loadingVolumeSnapshots || loadingVolumes}
                        columns={volumeSnapshotColumns}
                        onChange={getParamsUpdater('volumeSnapshot')}
                        value={params.volumeSnapshot}
                        uniqueIdentifier="id"
                        searchTargets={['name', 'description', 'status']}
                        checkboxCond={(volumeSnapshot) =>
                          volumeSnapshot.status === 'available' &&
                          volumesById[volumeSnapshot?.volume_id]?.bootable === 'true'
                        }
                        required
                      />
                    </>
                  )}
                </div>
              )}
              {step === 2 && !['image', 'snapshot'].includes(params.sourceType) && (
                <div className={classes.fields}>
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Attach Additional Volumes</Text>
                      <Text variant="body2">
                        Only volumes with <b>available</b> status may be selected below.
                      </Text>
                    </div>
                  </Info>
                  <Text variant="subtitle2">Volume</Text>
                  <ListTableField
                    id="volume"
                    data={availableVolumes}
                    onReload={reloadAvailableVolumes}
                    loading={loadingAvailableVolumes || updatingAvailableVolumes}
                    columns={volumeColumns}
                    onChange={getParamsUpdater('attachVolumes')}
                    value={params.attachVolumes}
                    uniqueIdentifier="id"
                    searchTargets={['name', 'status', 'volume_type', 'description']}
                    emptyText="No available volumes found"
                    multiSelection
                  />
                </div>
              )}
              {step === returnStepNumber(params.sourceType, 3) && (
                <div className={classes.fields}>
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Flavor Selection</Text>
                      <Text variant="body2">
                        Choose a flavor that defines the VM’s CPU, memory, disk, and optional GPU
                        settings.
                      </Text>
                    </div>
                  </Info>
                  <div /> {/* extra spacing */}
                  <div className={classes.filters}>
                    <ToggleSwitchField
                      id="enableHotplugging"
                      value={params.enableHotplugging}
                      onChange={(val) => {
                        updateParams({
                          enableHotplugging: val,
                          flavor: [],
                        })
                      }}
                      label="Hot-plug Compatible"
                    />
                    <ToggleSwitchField
                      id="gpuCompatible"
                      value={params.gpuCompatible}
                      onChange={(val) => {
                        updateParams({
                          gpuCompatible: val,
                          flavor: [],
                        })
                      }}
                      label="GPU Compatible"
                    />
                  </div>
                  {isVolumeSourceType && (
                    <Alert
                      variant="primary"
                      title="Supported Flavors"
                      message="Only zero-disk flavors are supported for volume-backed instances, as the root disk comes from the attached volume. As a result, the VM disk size will be equal to the volume size"
                    />
                  )}
                  {viewFlavorToggle === 'grid' && (
                    <PaginatedSelectableCards
                      renderSelectableCardComponent={renderFlavorCard}
                      data={filteredFlavors}
                      searchProps={['name']}
                      pluralLabel="Flavors"
                      extraHeaderContent={
                        <OptionsToggler
                          options={[
                            {
                              icon: 'list',
                              value: 'list',
                            },
                            {
                              icon: 'grid',
                              value: 'grid',
                            },
                          ]}
                          value={viewFlavorToggle}
                          onChange={setViewFlavorToggle}
                        />
                      }
                      sortDropdown={
                        <Dropdown
                          name="sort"
                          value={flavorSort}
                          items={[
                            {
                              label: 'Select...',
                              value: '',
                            },
                            {
                              label: 'Name',
                              value: 'name',
                            },
                            {
                              label: 'vCPUs',
                              value: 'vcpus',
                            },
                            {
                              label: 'RAM',
                              value: 'ram',
                            },
                            {
                              label: 'Disk',
                              value: 'disk',
                            },
                          ]}
                          onChange={(value) => setFlavorSort(value)}
                        ></Dropdown>
                      }
                    />
                  )}
                  {viewFlavorToggle === 'list' && (
                    <>
                      <div className={classes.spaceBelow}>
                        <OptionsToggler
                          options={[
                            {
                              icon: 'list',
                              value: 'list',
                            },
                            {
                              icon: 'grid',
                              value: 'grid',
                            },
                          ]}
                          value={viewFlavorToggle}
                          onChange={setViewFlavorToggle}
                        />
                      </div>
                      <ListTableField
                        id="flavor"
                        data={filteredFlavors}
                        loading={loadingFlavors}
                        columns={params.gpuCompatible ? flavorColumnsGpu : flavorColumns}
                        // visibleColumns to force table to update columns upon gpu switch toggle
                        visibleColumns={
                          params.gpuCompatible ? visibleGpuFlavorColumns : visibleFlavorColumns
                        }
                        onChange={getParamsUpdater('flavor')}
                        checkboxCond={(flavor) =>
                          !isFlavorCompatibleWithImageAndVolume(flavor, params)
                        }
                        checkboxCondMessage="Flavor disk size is smaller than the selected image"
                        value={params.flavor}
                        uniqueIdentifier="id"
                        searchTargets={['name']}
                        noScroll
                        required
                        emptyText={
                          isVolumeSourceType && filteredFlavors?.length === 0
                            ? 'No flavors with zero disk size are available. Please create a zero-disk-size flavor to proceed'
                            : 'No data found'
                        }
                      />
                    </>
                  )}
                  {params.enableHotplugging && (
                    <>
                      <div className={classes.separator} />
                      <div className={classes.halfColumns}>
                        <TextField
                          id="hotplugMemory"
                          label="Hotplug RAM (MB)"
                          onChange={(val) =>
                            updateParams({
                              hotplugMemory: val,
                            })
                          }
                          value={params.hotplugMemory}
                          type="number"
                          min={1}
                          info="The memory that will be hotplugged to the VM."
                          validations={[hotplugMemoryValidator, nonDecimalValueValidator()]}
                          required
                        />
                      </div>
                      <div className={classes.halfColumns}>
                        <TextField
                          id="hotplugVcpu"
                          label="Hotplug vCPUs"
                          onChange={(val) =>
                            updateParams({
                              hotplugVcpu: val,
                            })
                          }
                          value={params.hotplugVcpu}
                          type="number"
                          min={1}
                          info="The number of vCPUs that will be hotplugged to the VM."
                          validations={[hotplugVcpuValidator, nonDecimalValueValidator()]}
                          required
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
              {step === returnStepNumber(params.sourceType, 4) && (
                <div className={classes.fields}>
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Networking</Text>
                      <Text variant="body2">
                        Assign a network to the VM by selecting from available network pools.
                      </Text>
                    </div>
                  </Info>

                  {/* Select Network Type */}
                  <Text
                    variant="body2"
                    style={{ marginBottom: '16px', display: 'block', fontWeight: '600' }}
                  >
                    Select Network Type
                  </Text>
                  <div className={classes.networkModeCards}>
                    <div className={classes.networkCardWrapper}>
                      <SelectableCard
                        id="managed"
                        onClick={() => {
                          setVmNetworkMode(NetworkMode.MANAGED)
                          // Clear network selection when switching modes
                          if (params.networks.length > 0) {
                            getParamsUpdater('networks')([])
                          }
                        }}
                        active={vmNetworkMode === NetworkMode.MANAGED}
                        className={classes.networkCard}
                        withCustomBody
                      >
                        <div className={classes.networkCardBody}>
                          <Radio checked={vmNetworkMode === NetworkMode.MANAGED} />
                          <Text variant="caption1" className={classes.networkCardContent}>
                            Managed Networks ({managedNetworksCount})
                          </Text>
                          <Tooltip
                            customClassName={classes.networkTooltipBody}
                            message={MANAGED_NETWORK_INFO}
                          >
                            <HelpOutlineIcon className={classes.networkInfoIcon} />
                          </Tooltip>
                        </div>
                      </SelectableCard>
                    </div>
                    <div className={classes.networkCardWrapper}>
                      <SelectableCard
                        id="simple"
                        onClick={() => {
                          setVmNetworkMode(NetworkMode.SIMPLE)
                          // Clear network selection when switching modes
                          if (params.networks.length > 0) {
                            getParamsUpdater('networks')([])
                          }
                          // Disable security groups for Simple Networks (not supported)
                          if (params.useSecurityGroups) {
                            updateParams({ useSecurityGroups: false, securityGroups: [] })
                          }
                        }}
                        active={vmNetworkMode === NetworkMode.SIMPLE}
                        className={classes.networkCard}
                        withCustomBody
                      >
                        <div className={classes.networkCardBody}>
                          <Radio checked={vmNetworkMode === NetworkMode.SIMPLE} />
                          <Text variant="caption1" className={classes.networkCardContent}>
                            Layer 2 Networks ({simpleNetworksCount})
                          </Text>
                          <Tooltip
                            customClassName={classes.networkTooltipBody}
                            message={SIMPLE_NETWORK_TOOLTIP_INFO}
                          >
                            <HelpOutlineIcon className={classes.networkInfoIcon} />
                          </Tooltip>
                        </div>
                      </SelectableCard>
                    </div>
                  </div>

                  <ListTableField
                    id="networks"
                    data={filteredNetworksList}
                    loading={loadingNetworks}
                    columns={networkColumns}
                    onChange={(selectedNetworks) => {
                      setMinAvailableIpsNetwork(null)
                      getParamsUpdater('networks')(selectedNetworks)
                    }}
                    value={params.networks}
                    uniqueIdentifier="id"
                    searchTargets={['name']}
                    multiSelection
                    noScroll
                    required
                    checkboxCond={(network) => {
                      // If no network mode is set, allow all networks
                      if (vmNetworkMode === null) return true

                      // If Simple mode is active, only allow Simple networks (no IP check needed)
                      if (vmNetworkMode === NetworkMode.SIMPLE) {
                        return network.isSimpleNetwork
                      }

                      // If Managed mode is active, check available IPs/ports AND only allow Managed networks
                      if (vmNetworkMode === NetworkMode.MANAGED) {
                        // Check available IPs/ports for Managed networks only
                        if (network?.availablePortAndIPs <= 0) return false
                        return !network.isSimpleNetwork && network.subnets?.length > 0
                      }

                      return true
                    }}
                    checkboxCondMessage={
                      vmNetworkMode === NetworkMode.SIMPLE
                        ? 'Only Simple Networks can be selected in Simple Network mode'
                        : vmNetworkMode === NetworkMode.MANAGED
                        ? 'Managed Network has no available IP addresses or ports'
                        : 'Select a network mode first'
                    }
                  />
                </div>
              )}
              {step === returnStepNumber(params.sourceType, 5) && (
                <div className={classes.fields}>
                  <Info className={classes.info}>
                    <div className={classes.infoContent}>
                      <Text variant="caption1">Customize VM</Text>
                      <Text variant="body2">
                        Set advanced options like SSH keys, user data, security groups, VM metadata
                        and optional GPU lease time.
                      </Text>
                    </div>
                  </Info>

                  {/* Only show IP availability warning for Managed Networks (Simple Networks pre-create ports) */}
                  {minAvailableIpsNetwork?.availablePortAndIPs < params.count &&
                    vmNetworkMode === NetworkMode.MANAGED && (
                      <Alert
                        variant="warning"
                        message={
                          <Text variant="body2">
                            Not enough available IPs or ports. Managed Network
                            <Text variant="caption1"> {minAvailableIpsNetwork?.name} </Text>
                            only contains {minAvailableIpsNetwork?.availablePortAndIPs} available
                            IPs or ports. Please select another network or select VM count between 1
                            and {minAvailableIpsNetwork?.availablePortAndIPs}.
                          </Text>
                        }
                      />
                    )}
                  {disableMultipleVMs.disabled && (
                    <Alert
                      variant="warning"
                      message={<Text variant="body2">{disableMultipleVMs.message}</Text>}
                    />
                  )}

                  <div>
                    <Text variant="body2" component="div" className={classes.tooltipContainer}>
                      Number of VMs
                      <HintTooltip message="Specify the number of VMs you’d like to create using this configuration" />
                    </Text>
                    <div
                      className={clsx(classes.vmCount, {
                        [classes.vmCountDisabled]: disableMultipleVMs.disabled,
                      })}
                    >
                      <Slider
                        min={1}
                        max={disableMultipleVMs.disabled ? 1 : 20}
                        minLabel="1 VM"
                        maxLabel="20 VMs"
                        step={1}
                        onChange={(val) =>
                          updateParams({
                            count: val,
                          })
                        }
                        value={params.count}
                      />
                      <div className={classes.vmCountInput}>
                        <TextField
                          id="count"
                          label=""
                          onChange={(val) =>
                            updateParams({
                              count: val,
                            })
                          }
                          value={params.count}
                          type="number"
                          min={1}
                          max={20}
                          disabled={disableMultipleVMs.disabled}
                          validations={[countValidator]}
                          required
                        />
                        <Text variant="body2" className={classes.vmCountText}>
                          VMs
                        </Text>
                      </div>
                    </div>
                  </div>
                  <div className={classes.halfColumns}>
                    <PicklistField
                      id="sshKey"
                      DropdownComponent={SshKeyPicklist}
                      value={params.sshKey}
                      onChange={getParamsUpdater('sshKey')}
                      // @ts-ignore
                      info={
                        <div>
                          Select the ssh key that will be injected into the VM to enable ssh access
                        </div>
                      }
                    />
                    <PicklistField
                      id="serverGroup"
                      DropdownComponent={ServerGroupsPicklist}
                      value={params.serverGroup}
                      onChange={getParamsUpdater('serverGroup')}
                      info={
                        <div>
                          Select what VM affinity or anti-affinity group this VM should be part of.{' '}
                          <ExternalLink className={classes.tooltipLink} url={serverGroupsDoc}>
                            More info
                          </ExternalLink>
                        </div>
                      }
                    />
                  </div>
                  <div className={classes.separator} />
                  <ToggleSwitchField
                    id="useCloudInit"
                    value={params.useCloudInit}
                    onChange={getParamsUpdater('useCloudInit')}
                    label="Use Cloud-init"
                    textDescription={
                      'Add user data for initial VM setup, such as installing packages, or configuring users. This will run on first boot.'
                    }
                  />
                  {params.useCloudInit && (
                    <div className={classes.tabbed}>
                      <CodeMirror
                        variant="light"
                        label="Cloud-init YAML"
                        id="cloudInitYaml"
                        showCopyButton
                        onChange={getParamsUpdater('cloudInitYaml')}
                        value={params.cloudInitYaml}
                        validations={[yamlValidator]}
                      />
                    </div>
                  )}
                  <ToggleSwitchField
                    id="useSecurityGroups"
                    value={params.useSecurityGroups}
                    onChange={getParamsUpdater('useSecurityGroups')}
                    label="Assign Security Groups"
                    // Disable Security Groups when Simple Networks are selected (not supported)
                    disabled={vmNetworkMode === NetworkMode.SIMPLE}
                    textDescription={
                      'Choose one or more security groups to be applied to the VM, to ensure consistent traffic rules across all network interfaces attached to the VM.'
                    }
                  />
                  {params.useSecurityGroups && vmNetworkMode !== NetworkMode.SIMPLE && (
                    <div className={classes.tabbed}>
                      <ListTableField
                        id="securityGroups"
                        data={securityGroupsList}
                        loading={loadingSecurityGroups}
                        columns={securityGroupColumns}
                        onChange={getParamsUpdater('securityGroups')}
                        value={params.securityGroups}
                        uniqueIdentifier="id"
                        searchTargets={['name']}
                        multiSelection
                        required
                      />
                    </div>
                  )}
                  <div className={classes.separator} />
                  <Text variant="subtitle2">Set Migration Priority</Text>
                  <Text variant="body2">
                    The migration priority determines whether the virtual machine qualifies as a
                    candidate for migration by the DRR service, and, if it does, specifies the
                    priority level for its migration.{' '}
                    <ExternalLink url={drrDoc}>More info</ExternalLink>
                  </Text>
                  <PicklistField
                    id="priority"
                    DropdownComponent={MigrationPriorityPicklist}
                    value={params.priority}
                    onChange={getParamsUpdater('priority')}
                  />
                  <div className={classes.separator} />
                  <KeyValuesField
                    id="metadata"
                    label="Metadata"
                    value={params.metadata}
                    onChange={getParamsUpdater('metadata')}
                    addLabel="Add Metadata"
                    allowMultipleValues
                  />
                </div>
              )}
            </>
          ) : (
            <div className={classes.passwordScreen}>
              <VmCredentialsPanel vmPassword={vmPassword} />
            </div>
          )}
        </>
      )}
    </ModalForm>
  )
}
